import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { Plus, Minus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';

export const CartPage = () => {
  const { cartItems, updateQuantity, removeFromCart, getCartTotal } = useCart();
  const subtotal = getCartTotal();
  const shipping = subtotal > 0 ? 5.99 : 0;
  const total = subtotal + shipping;

  const colorMap = {
    'herb-savory': '#2D5016',
    'citrus-garlic': '#D4540A',
    'smoky-savory': '#5C3D2E',
    'seafood-seasoning': '#1E4D6B',
    'full-set': '#2A2A2A',
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex flex-col items-center justify-center px-4" data-testid="empty-cart">
        <ShoppingBag className="w-16 h-16 text-[#CCC] mb-4" />
        <h1 className="font-['Bodoni_Moda'] text-3xl font-bold text-[#2A2A2A] mb-2">
          Your cart is empty
        </h1>
        <p className="text-[#666] mb-8 text-center">
          Time to add some Bo to your life.
        </p>
        <Link to="/shop">
          <Button className="bg-[#D4540A] hover:bg-[#B84709] text-white px-8 py-6 text-lg font-semibold" data-testid="shop-now-btn">
            Shop the Blends
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-['Bodoni_Moda'] text-3xl sm:text-4xl font-bold text-[#2A2A2A] mb-8">
          Your Cart
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => {
              const accentColor = colorMap[item.id] || '#D4540A';
              const isBundle = item.is_bundle || item.id === 'full-set';
              return (
                <div
                  key={item.id}
                  className="bg-white rounded-xl p-4 sm:p-6 border border-[#E5E0D8] flex flex-col sm:flex-row gap-4"
                  data-testid={`cart-item-${item.id}`}
                >
                  {/* Thumbnail - Bundle shows proper 4-bottle image */}
                  <div className={`w-20 h-24 sm:w-24 sm:h-28 rounded-lg flex-shrink-0 mx-auto sm:mx-0 overflow-hidden ${isBundle ? 'bg-[#3D2B1F] p-1' : ''}`}>
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className={`w-full h-full ${isBundle ? 'object-contain' : 'object-cover'}`}
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-['Bodoni_Moda'] text-lg font-bold text-[#2A2A2A]">
                          {item.name}
                        </h3>
                        <p className="text-sm font-medium" style={{ color: accentColor }}>
                          {item.subtitle}
                          {isBundle && <span className="ml-2 text-xs bg-[#2A2A2A] text-white px-2 py-0.5 rounded">4 bottles</span>}
                        </p>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-[#999] hover:text-red-500 transition-colors p-1"
                        data-testid={`remove-${item.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-auto pt-4">
                      {/* Quantity */}
                      <div className="flex items-center border border-[#E5E0D8] rounded-lg">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-2 hover:bg-[#F5F3F0] transition-colors"
                          data-testid={`decrease-${item.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="px-4 font-medium" data-testid={`qty-${item.id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-2 hover:bg-[#F5F3F0] transition-colors"
                          data-testid={`increase-${item.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <p className="font-bold text-[#2A2A2A]" data-testid={`subtotal-${item.id}`}>
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                        <p className="text-sm text-[#666]">
                          ${item.price.toFixed(2)} each
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 border border-[#E5E0D8] sticky top-24" data-testid="order-summary">
              <h2 className="font-['Bodoni_Moda'] text-xl font-bold text-[#2A2A2A] mb-6">
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-[#666]">Subtotal</span>
                  <span className="font-medium text-[#2A2A2A]" data-testid="cart-subtotal">
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#666]">Shipping</span>
                  <span className="font-medium text-[#2A2A2A]" data-testid="cart-shipping">
                    ${shipping.toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-[#E5E0D8] pt-4">
                  <div className="flex justify-between">
                    <span className="font-semibold text-[#2A2A2A]">Total</span>
                    <span className="font-bold text-xl text-[#2A2A2A]" data-testid="cart-total">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <Link to="/checkout">
                <Button
                  className="w-full bg-[#D4540A] hover:bg-[#B84709] text-white py-6 text-lg font-semibold"
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>

              <Link to="/shop" className="block mt-4">
                <Button variant="outline" className="w-full border-[#E5E0D8]" data-testid="continue-shopping">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
